import { http } from '@/helpers/http'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { Speaker, VoiceTTS, type TAiVoice, type TVoiceResponse } from '@/types/voice'
import { Node } from '@tiptap/pm/model'
import type { Editor } from '@tiptap/core'
import { track } from '@/helpers/mixpanelDes'
import { useAiTtsStore } from '@/stores/ai-tts'
import { useEditorStore } from '@/stores/editor'
import { usePopupStore } from '@/stores/popup'
import { allLanguages } from '@/config/languages'

export const useVoiceStore = defineStore('voices', () => {
  const aiTtsStore = useAiTtsStore()

  const breakStrength = [
    { label: 'None', name: 'none' },
    { label: 'X-weak', name: 'x-weak' },
    { label: 'Weak', name: 'weak' },
    { label: 'Default', name: 'medium' },
    { label: 'Strong', name: 'strong' },
    { label: 'X-strong', name: 'x-strong' },
  ]

  const languages = computed(() => {
    return allLanguages.filter((l) => voices.value.some((v) => v.Locale === l.name))
  })

  const genders = ref([
    { name: 'male', label: '👨 Male' },
    { name: 'female', label: '👩 Female' },
  ])
  const voices = ref<VoiceTTS[]>([])
  const azureVoices = ref<VoiceTTS[]>([])
  const mainSpeaker = ref<Speaker | null>(null)
  const currentSpeaker = ref<Speaker | null>(null)
  const documentSpeakers = ref<Speaker[]>([])
  const savedSpeakers = ref<{ name: string; pitch: number; speed: number; style: string; customName: string | null }[]>(
    [],
  )
  const player = new Audio()
  const playerVoice = ref<Speaker | null>(null)

  function mergeVoices() {
    const aiVoices = aiTtsStore.voices.map((voice) => new VoiceTTS(voice, allLanguages))
    voices.value = [...azureVoices.value, ...aiVoices]
  }

  async function getVoices() {
    try {
      const [azureRes, _] = await Promise.all([
        http.get<TVoiceResponse[]>('/tts/list_voices'),
        aiTtsStore.fetchVoices(),
      ])

      azureVoices.value = azureRes.map((voice) => new VoiceTTS(voice, allLanguages))
      mergeVoices()
    } catch (error) {
      console.error('Error during voices loading:', error)
      azureVoices.value = []
      voices.value = []
    }
  }

  watch(
    () => aiTtsStore.voices,
    () => {
      mergeVoices()
    },
    { deep: true },
  )

  const defaultSpeaker = computed(() => {
    const voiceName = 'en-US-JennyNeural'
    const voice = voices.value.find((v) => v.Name === voiceName)

    if (!voice) {
      return null
    }

    return new Speaker(voiceName, 0, 0, 'Default', null, voice)
  })

  function findByName(name: string): VoiceTTS | undefined {
    return voices.value.find((v) => v.Name === name)
  }

  function refreshAiVoice(voice: TAiVoice): void {
    mergeVoices()

    const voiceTTS = findByName(voice._id)

    if (!voiceTTS) return

    const speakers = [mainSpeaker.value, ...documentSpeakers.value]

    for (const speaker of speakers) {
      if (speaker?.voiceTTS.originId === voice._id) speaker.voiceTTS = voiceTTS
    }
  }

  const preferredLocales: Record<string, string> = {
    en: 'en-US',
    pt: 'pt-BR',
  }

  function localeForLanguage(language: string | null | undefined): string | null {
    if (!language) {
      return null
    }

    const code = language.toLowerCase()
    const matches = languages.value.filter((l) => l.name.toLowerCase().split('-')[0] === code)

    if (!matches.length) {
      return null
    }

    const preferred = matches.find((l) => l.name === preferredLocales[code])
    const home = matches.find((l) => l.name.split('-')[1]?.toLowerCase() === code)

    return (preferred ?? home ?? matches[0]).name
  }

  function speakerForLanguage(language: string | null | undefined): Speaker | null {
    const locale = localeForLanguage(language)

    if (!locale) {
      return defaultSpeaker.value
    }

    const voice = voices.value.find((v) => !v.isAi && v.Locale === locale)

    if (!voice) {
      return defaultSpeaker.value
    }

    return new Speaker(voice.Name, 0, 0, 'Default', null, voice)
  }

  function readDocumentSpeakers(editor: Editor): void {
    documentSpeakers.value = []

    if (mainSpeaker.value) {
      documentSpeakers.value.push(mainSpeaker.value)
    } else if (defaultSpeaker.value) {
      documentSpeakers.value.push(defaultSpeaker.value)
    }

    editor.state.doc.descendants((node) => {
      // console.log('>>', node.type, node.type.name, node.innerText)

      if (node.type.name === 'audiobookParagraph') {
        const speaker = getSpeakerFromNode(node)

        if (speaker) {
          const alreadyAdded = documentSpeakers.value.find((e) => {
            return (
              e.displayName === speaker.displayName &&
              e.gender === speaker.gender &&
              e.locale === speaker.locale &&
              e.name === speaker.name
            )
          })

          if (!alreadyAdded) documentSpeakers.value.push(speaker)
        }
      }
    })

    for (const saved of savedSpeakers.value) {
      const voiceTTS = findByName(saved.name)
      if (!voiceTTS) continue
      const alreadyAdded = documentSpeakers.value.find((e) => e.name === saved.name)
      if (!alreadyAdded) {
        documentSpeakers.value.push(
          new Speaker(saved.name, saved.pitch, saved.speed, saved.style, saved.customName, voiceTTS),
        )
      }
    }
  }

  function replaceAllSpeakers(editor: Editor, newSpeaker: Speaker): void {
    const { tr } = editor.state
    let hasChanges = false

    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'audiobookParagraph') {
        const newAttrs = {
          ...node.attrs,
          voice: newSpeaker.voiceTTS.isAi ? newSpeaker.voiceTTS.originId : newSpeaker.name,
          style: newSpeaker.style,
          pitch: newSpeaker.pitch,
          speed: newSpeaker.speed,
          customName: newSpeaker.customName,
          voiceTTS: newSpeaker.voiceTTS,
          id: newSpeaker.voiceTTS.isAi ? newSpeaker.voiceTTS.originId : null,
        }

        if (JSON.stringify(node.attrs) !== JSON.stringify(newAttrs)) {
          tr.setNodeMarkup(pos, undefined, newAttrs)
          hasChanges = true
        }
      }
    })

    if (hasChanges) {
      editor.view.dispatch(tr)

      readDocumentSpeakers(editor)
    }
  }

  async function setMainSpeaker(speaker: Speaker): Promise<boolean> {
    const popup = usePopupStore()
    const editorStore = useEditorStore()
    let replaceAll = false

    if (mainSpeaker.value?.voiceTTS.isAi !== speaker.voiceTTS.isAi) {
      const current = mainSpeaker.value?.voiceTTS.isAi ? 'your own voice' : 'external voice'
      const target = mainSpeaker.value?.voiceTTS.isAi ? 'external voice' : 'your own voice'

      const res = await popup.confirm(`Switch all speakers to ${target}?`, {
        content: `Currently you're using ${current}. This will apply to every speaker.`,
        labelConfirm: `Switch to ${target}`,
      })

      if (!res) return false

      replaceAll = true
    }

    mainSpeaker.value = speaker

    if (replaceAll) {
      const editor = editorStore.getEditor()
      if (editor) replaceAllSpeakers(editor, speaker)
    }

    track('audiobooks-speaker-set-main', { displayName: speaker.displayName, voice: speaker.voiceTTS.Name })

    return true
  }

  function fillMissingSpeakers(editor: Editor): void {
    const speaker = mainSpeaker.value || defaultSpeaker.value
    if (!speaker) {
      return
    }

    const defaultVoice = speaker.voiceTTS.isAi ? speaker.voiceTTS.originId : speaker.name
    const { tr } = editor.state
    let hasChanges = false

    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'audiobookParagraph' && (node.attrs.voice == null || node.attrs.voice === '')) {
        const newAttrs = {
          ...node.attrs,
          voice: defaultVoice,
          name: speaker.displayName,
          pitch: speaker.pitch,
          speed: speaker.speed,
          style: speaker.style,
          customName: speaker.customName,
          voiceTTS: speaker.voiceTTS,
          id: speaker.voiceTTS.isAi ? speaker.voiceTTS.originId : null,
        }

        if (JSON.stringify(node.attrs) !== JSON.stringify(newAttrs)) {
          tr.setNodeMarkup(pos, undefined, newAttrs)
          hasChanges = true
        }
      }
    })

    if (hasChanges) {
      editor.view.dispatch(tr)
      readDocumentSpeakers(editor)
    }
  }

  function play(voice: Speaker) {
    const sample = voice.style

    player.oncanplay = player.play
    player.onerror = () => {
      player.src = ''
      playerVoice.value = null
    }

    player.src = voice.voiceTTS.Samples[sample]!
    playerVoice.value = voice
  }

  function pause() {
    player.pause()
    player.src = ''
    playerVoice.value = null
  }

  function getSpeakerFromNode(node: Node): Speaker | null {
    const { voice, style, speed, pitch, customName } = node.attrs ?? {}
    const voiceTTS = findByName(voice)

    if (voiceTTS) {
      return new Speaker(voice, ~~pitch, ~~speed, style, customName || null, voiceTTS)
    }

    return null
  }

  return {
    getVoices,
    languages,
    breakStrength,
    genders,
    voices,
    defaultSpeaker,
    mainSpeaker,
    currentSpeaker,
    documentSpeakers,
    savedSpeakers,
    findByName,
    refreshAiVoice,
    localeForLanguage,
    speakerForLanguage,
    readDocumentSpeakers,
    getSpeakerFromNode,
    play,
    pause,
    playerVoice,
    replaceAllSpeakers,
    setMainSpeaker,
    fillMissingSpeakers,
  }
})
